import API from '../../../helpers/api';

import { message } from '../../../utils/message';

import { load as loadTotalBalance } from '../../client/actions/TotalBalance';
import FILES from '../actionTypes';
import {
  AI_ATLAS_FILE_ADD_MUTATION,
  AI_FILE_ADD_MUTATION,
  AI_FILE_CHANGE_STATUS_MUTATION,
  AI_FILE_GENERATE_MUTATION,
  AI_FILE_REMOVE_MUTATION,
  AI_FILE_RENAME_MUTATION,
  AI_FILE_UPLOAD_MUTATION,
  AI_FILES_GET_QUERY,
} from '../queries';

import { AppDispatch, GetState } from '../../../types/app';
import { AiFileItem, AiFilesGetResponse, AiFileUpdateStatus } from '../types';

export const loadFiles = () => async (dispatch: AppDispatch) => {
  dispatch({ type: FILES.LOAD.REQUESTING });

  try {
    const { aiFilesGet } = await API.callGraphQL<{
      aiFilesGet: AiFilesGetResponse;
    }>(AI_FILES_GET_QUERY, {
      Limit: 1000,
      Offset: 0,
      SortBy: 'Id',
      SortDir: 'DESC',
    });

    dispatch({
      type: FILES.LOAD.SUCCESS,
      count: aiFilesGet.count,
      rows: aiFilesGet.rows,
    });

    return aiFilesGet;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);

    console.error(err);
    dispatch({ type: FILES.LOAD.FAILURE, err: message });

    return undefined;
  }
};

export const loadAllFiles = () => async (dispatch: AppDispatch) =>
  dispatch(loadFiles());

export const loadIfNeeded =
  () => async (dispatch: AppDispatch, getState: GetState) => {
    const { Files } = getState();

    if (Files.readyStatus === FILES.LOAD.INVALID) {
      await dispatch(loadFiles());
    }
  };

export const loadFilesSilent = () => async (dispatch: AppDispatch) => {
  try {
    const { aiFilesGet } = await API.callGraphQL<{
      aiFilesGet: AiFilesGetResponse;
    }>(AI_FILES_GET_QUERY, {
      Limit: 1000,
      Offset: 0,
      SortBy: 'Id',
      SortDir: 'DESC',
    });

    dispatch({
      type: FILES.LOAD.SUCCESS,
      count: aiFilesGet.count,
      rows: aiFilesGet.rows,
    });

    return aiFilesGet;
  } catch (err) {
    console.error(err);

    return undefined;
  }
};

export const addFile =
  (name: string, file: File) => async (dispatch: AppDispatch) => {
    const trimmedName = name.trim();

    if (!trimmedName || trimmedName.length > 255) {
      throw new Error('Invalid file name');
    }

    const { aiFileAdd } = await API.callGraphQL<{ aiFileAdd: number }>(
      AI_FILE_ADD_MUTATION,
      { Name: trimmedName, Content: null },
      { Content: file }
    );

    await dispatch(loadAllFiles());
    message.success('files.upload.success');

    return aiFileAdd;
  };

export const generateAiFile =
  (name: string) => async (dispatch: AppDispatch) => {
    const trimmedName = name.trim();

    if (!trimmedName || trimmedName.length > 255) {
      throw new Error('Invalid file name');
    }

    const { aiFileGenerate } = await API.callGraphQL<{
      aiFileGenerate: string;
    }>(AI_FILE_GENERATE_MUTATION);

    if (!aiFileGenerate?.trim()) {
      throw new Error('Generated CSV is empty');
    }

    const csvFile = new File([aiFileGenerate], `${trimmedName}.csv`, {
      type: 'text/csv',
    });

    await API.callGraphQL<{ aiFileAdd: number }>(
      AI_FILE_ADD_MUTATION,
      { Name: trimmedName, Content: null },
      { Content: csvFile }
    );

    await dispatch(loadAllFiles());
    message.success('files.aiGenerate.success');
  };

export const addEmptyAtlasFile =
  (name: string) => async (dispatch: AppDispatch) => {
    const trimmedName = name.trim();

    if (!trimmedName || trimmedName.length > 255) {
      throw new Error('Invalid file name');
    }

    const { aiFileAdd } = await API.callGraphQL<{
      aiFileAdd: number | string;
    }>(AI_ATLAS_FILE_ADD_MUTATION, { Name: trimmedName });
    const files = await dispatch(loadAllFiles());
    const createdFile =
      files?.rows.find(
        (file: AiFileItem) =>
          file.Id === Number(aiFileAdd) || file.Key === String(aiFileAdd)
      ) || files?.rows.find((file: AiFileItem) => file.Name === trimmedName);

    if (!createdFile) {
      throw new Error('Created file not found');
    }

    message.success('files.atlasGenerate.success');

    return createdFile;
  };

export const uploadFile =
  (id: number, file: File) => async (dispatch: AppDispatch) => {
    const { aiFileUpload } = await API.callGraphQL<{ aiFileUpload: boolean }>(
      AI_FILE_UPLOAD_MUTATION,
      { Id: id, Content: null },
      { Content: file }
    );

    await dispatch(loadAllFiles());
    message.success('files.replace.success');

    return aiFileUpload;
  };

export const renameFile =
  (id: number, name: string) => async (dispatch: AppDispatch) => {
    const trimmedName = name.trim();

    if (!trimmedName || trimmedName.length > 255) {
      throw new Error('Invalid file name');
    }

    const { aiFileRename } = await API.callGraphQL<{ aiFileRename: boolean }>(
      AI_FILE_RENAME_MUTATION,
      { Id: id, Name: trimmedName }
    );

    await dispatch(loadAllFiles());
    message.success('files.rename.success');

    return aiFileRename;
  };

export const removeFile = (id: number) => async (dispatch: AppDispatch) => {
  const { aiFileRemove } = await API.callGraphQL<{ aiFileRemove: boolean }>(
    AI_FILE_REMOVE_MUTATION,
    { Id: id }
  );

  await dispatch(loadAllFiles());
  message.success('files.remove.success');

  return aiFileRemove;
};

export const changeFileStatus =
  (id: number, status: AiFileUpdateStatus) => async (dispatch: AppDispatch) => {
    const { aiFileChangeStatus } = await API.callGraphQL<{
      aiFileChangeStatus: boolean;
    }>(AI_FILE_CHANGE_STATUS_MUTATION, {
      Id: id,
      Status: status,
    });

    await dispatch(loadAllFiles());

    if (status === 'Pending') {
      await dispatch(loadTotalBalance());
    }

    message.success('files.changeStatus.success');

    return aiFileChangeStatus;
  };
